/**
 * /pipeline-facility/anomalies/new and /pipeline-facility/anomalies/:id —
 * one ILI anomaly (POST / GET / PATCH / DELETE /pipeline-facility/anomalies).
 *
 * A duplicate is a 409 naming the existing anomaly (`duplicate_anomaly`, or
 * `feature_id_conflict` when the same feature ID sits at another position);
 * the refusal is said in a sentence and links to the existing one. A delete is
 * confirmed inline and refused while a dig feature points at it
 * (`anomaly_in_use`, naming the digs).
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { usePfMeta, usePfSites } from '../../../hooks/usePipelineFacility';
import { useCreatePfAnomaly, useDeletePfAnomaly, usePfAnomaly, useUpdatePfAnomaly } from '../../../hooks/usePfDigs';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canDispatchPf, fmtNumber, pfDay, withUnit } from '../../../lib/pipeline-facility';
import { pfDigRefusal, refusalExistingId } from '../../../lib/pf-digs';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PfEmpty, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { AnomalyForm } from './AnomalyForm';
import { PfInlineConfirm } from './DigBits';

function ExistingLink({ id }: { id: string | null }) {
  if (!id) return null;
  return (
    <Link to={`/pipeline-facility/anomalies/${id}`} className="inline-block text-sm font-bold text-fi-amber underline" data-testid="pf-anomaly-existing-link">
      Open the existing anomaly
    </Link>
  );
}

export function PfNewAnomalyScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/pipeline-facility/anomalies');
  const catalog = usePfMeta().data?.data ?? null;
  const sitesQuery = usePfSites();
  const create = useCreatePfAnomaly();
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  return (
    <div data-testid="pf-anomaly-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={PF_EYEBROW} title="Add anomaly" description="One feature from the operator's ILI list." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <PfPanel eyebrow="Anomaly" title="New anomaly">
            <AnomalyForm
              sites={sitesQuery.data?.data ?? []}
              defaultSiteId={searchParams.get('siteId') ?? ''}
              catalog={catalog}
              meta={sitesQuery.data?.meta ?? null}
              pending={create.isPending}
              error={error}
              errorAction={<ExistingLink id={existingId} />}
              submitLabel="Add anomaly"
              onCancel={back}
              onSubmit={(body) => {
                setError(null);
                setExistingId(null);
                create.mutate(body, {
                  onSuccess: (r) => {
                    toast.success('Anomaly added');
                    navigate(r.data ? `/pipeline-facility/anomalies/${r.data.id}` : '/pipeline-facility/anomalies', { replace: true });
                  },
                  onError: (err) => {
                    setError(pfDigRefusal(err, 'The anomaly could not be added.'));
                    setExistingId(refusalExistingId(err));
                  },
                });
              }}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}

export default function PfAnomalyDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/pipeline-facility/anomalies');
  const { user } = useAuth();
  const canWrite = canDispatchPf(user?.role);
  const query = usePfAnomaly(id);
  const catalog = usePfMeta().data?.data ?? null;
  const update = useUpdatePfAnomaly();
  const del = useDeletePfAnomaly();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const a = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const u = meta?.units ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="pf-anomaly-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title={a?.featureId || (query.isLoading ? 'Anomaly' : '—')}
        description={a ? `${a.featureTypeLabel ?? a.featureType} · ${a.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          a && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading anomaly" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !a) ? (
            <PfPanel eyebrow="Anomaly" title="Anomaly">
              <PfEmpty>This anomaly was not found. It may have been deleted.</PfEmpty>
            </PfPanel>
          ) : failure || !a ? (
            <PfPanel eyebrow="Anomaly" title="Anomaly">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this anomaly" />
            </PfPanel>
          ) : (
            <>
              {deleting && (
                <PfInlineConfirm
                  testId="pf-anomaly-delete-confirm"
                  message={`Delete ${a.featureId ?? 'this anomaly'} from the site's list? An anomaly that is a feature on a dig cannot be deleted.`}
                  confirmLabel="Delete anomaly"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(a.id, {
                      onSuccess: () => {
                        toast.success('Anomaly deleted');
                        navigate(`/pipeline-facility/anomalies?siteId=${a.siteId}`, { replace: true });
                      },
                      onError: (err) => setDeleteError(pfDigRefusal(err, 'The anomaly could not be deleted.')),
                    })
                  }
                />
              )}
              <PfPanel eyebrow="ILI anomaly" title="Details" testId="pf-anomaly-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Feature ID" value={a.featureId || '—'} mono />
                  <PfSpec label="Type" value={a.featureTypeLabel || a.featureType || '—'} />
                  <PfSpec label="Site" value={a.siteName ? <Link className="underline" to={`/pipeline-facility/sites/${a.siteId}`}>{a.siteName}</Link> : '—'} />
                  <PfSpec label="Segment" value={a.segmentLabel || '—'} />
                  <PfSpec label="Chainage" value={withUnit(a.chainage, u?.chainage)} mono />
                  <PfSpec label="Odometer" value={withUnit(a.odometer, u?.chainage)} mono />
                  <PfSpec label="Clock" value={a.clockPosition || '—'} mono />
                  <PfSpec label="Depth" value={a.depthPctWt == null ? '—' : `${fmtNumber(a.depthPctWt)}% WT`} mono />
                  <PfSpec label="Length" value={withUnit(a.length, u?.wallThickness)} mono />
                  <PfSpec label="Width" value={withUnit(a.width, u?.wallThickness)} mono />
                  <PfSpec label="Priority" value={a.priority || '—'} />
                  <PfSpec label="ILI vendor" value={a.iliVendor || '—'} />
                  <PfSpec label="ILI run" value={pfDay(a.iliRunDate)} />
                  <PfSpec label="Latitude" value={fmtNumber(a.latitude)} mono />
                  <PfSpec label="Longitude" value={fmtNumber(a.longitude)} mono />
                  <PfSpec label="Dig" value={a.digNumber || 'Not on a dig'} mono />
                </dl>
                {a.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{a.notes}</p>}
              </PfPanel>
              {editing && canWrite && (
                <PfPanel eyebrow="Edit" title="Edit anomaly">
                  <AnomalyForm
                    anomaly={a}
                    sites={[]}
                    catalog={catalog}
                    meta={meta}
                    pending={update.isPending}
                    error={editError}
                    errorAction={<ExistingLink id={existingId} />}
                    submitLabel="Save anomaly"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      setExistingId(null);
                      update.mutate(
                        { id: a.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Anomaly saved');
                            setEditing(false);
                          },
                          onError: (err) => {
                            setEditError(pfDigRefusal(err, 'The anomaly could not be saved.'));
                            setExistingId(refusalExistingId(err));
                          },
                        },
                      );
                    }}
                  />
                </PfPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
